package navigation.settings.imagegallery.imageInfos

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalUriHandler
import com.essntl.features.file.ui.imageinfos.ImageInfosEvent
import com.essntl.features.file.ui.imageinfos.ImageInfosState
import com.essntl.features.proposal.domain.model.shareLink
import com.essntl.uikit.components.card.EsExpandableCardsSection
import com.essntl.uikit.components.iconbutton.outline.EsSmallOutlineIconButton
import com.essntl.uikit.components.iconbutton.primary.EsSmallPrimaryIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.rememberFind
import com.essntl.uikit.rememberNumberOfDaysPrettyString
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import core.dateformat.ui.formatDate
import core.dateformat.ui.formatDateRange
import navigation.settings.imagegallery.imageInfos.components.DeleteNonUsedImage
import navigation.settings.imagegallery.imageInfos.components.KeywordsImage
import kotlin.collections.orEmpty

@Composable
fun ImageInfosScreen(
    state: ImageInfosState,
    onEvent: (ImageInfosEvent) -> Unit,
    navigateToProposal: (String) -> Unit,
) {
    val isLoading = remember(state) { state.isLoading }

    val isFailure = remember(state) { state.isFailure }

    val isDeletable = remember(state) { state.isDeletable }

    val uriHandler = LocalUriHandler.current

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(1f.spacing)
            .background(
                color = MaterialTheme.esColorScheme.bgSecondary,
                shape = RoundedCornerShape(EsConstants.RoundedCornerSize),
            )
            .verticalScroll(state = rememberScrollState()),
    ) {
        if (isLoading ||
            isFailure
        ) {
            if (isLoading)
                EsSpinner(
                    modifier = Modifier
                        .padding(2.5f.spacing)
                        .align(Alignment.CenterHorizontally),
                )
            else
                Text(
                    text = "Something goes wrong!",
                    color = MaterialTheme.esColorScheme.textSecondary,
                    style = MaterialTheme.esTypography.p1Medium16,
                    modifier = Modifier.padding(start = 2.5f.spacing),
                )
        } else {
            Spacer(Modifier.height(height = 1.spacing))
            KeywordsImage(
                state = state,
                onEvent = onEvent,
            )

            Spacer(Modifier.height(height = 2.spacing))

            state.getAllProposals.data?.let { proposals ->
                if (proposals.isNotEmpty()) {
                    Text(
                        text = "Proposals",
                        style = MaterialTheme.esTypography.h565018.copy(
                            color = MaterialTheme.esColorScheme.textPrimary,
                        ),
                        modifier = Modifier.padding(start = 2.5f.spacing),
                    )

                    EsExpandableCardsSection(
                        items = proposals,
                        title = "",
                        cardTitle = { it.title ?: "" },
                        cardSubtitle = {
                            listOf(it.fromDate, it.toDate).formatDateRange() +
                                '\n' + rememberNumberOfDaysPrettyString(it.fromDate, it.toDate)
                        },
                        cardActionButtons = {
                            EsSmallOutlineIconButton(
                                onClick = {
                                    uriHandler.openUri(
                                        uri = it.shareLink,
                                    )
                                },
                                iconRes = EsIcons.Browse,
                            )

                            EsSmallPrimaryIconButton(
                                onClick = {
                                    navigateToProposal(it.id)
                                },
                                iconRes = EsIcons.Edit,
                            )
                        },
                    )
                } else if (state.getCountProposalsHistory.data?.toInt() != 0)
                    Text(
                        text = "This cover image has proposals history, but no proposals"
                            + '\n' +
                            "You can't delete it",
                        style = MaterialTheme.esTypography.h565018.copy(
                            color = MaterialTheme.esColorScheme.textPrimary,
                        ),
                        modifier = Modifier.padding(
                            start = 2.5f.spacing,
                            bottom = 2.5f.spacing,
                        ),
                    )
            }

            state.getAllServices.data?.let { services ->
                if (services.isNotEmpty()) {
                    Text(
                        text = "Services",
                        style = MaterialTheme.esTypography.h565018.copy(
                            color = MaterialTheme.esColorScheme.textPrimary,
                        ),
                        modifier = Modifier.padding(start = 2.5f.spacing),
                    )

                    EsExpandableCardsSection(
                        items = services,
                        title = "",
                        cardTitle = { it.title },
                        cardSubtitle = {
                            val category = rememberFind(
                                state.getAllCategories.data.orEmpty(),
                                it.category,
                            ) { cat -> cat.id }

                            category?.name.orEmpty() + '\n' +
                                it.createdAt.formatDate(".")
                        },
                        cardActionButtons = {},
                    )
                }
            }

            state.getAllTravelAgents.data?.let { travelAgents ->
                if (travelAgents.isNotEmpty()) {
                    Text(
                        text = "Travel Agents",
                        style = MaterialTheme.esTypography.h565018.copy(
                            color = MaterialTheme.esColorScheme.textPrimary,
                        ),
                        modifier = Modifier.padding(start = 2.5f.spacing),
                    )

                    EsExpandableCardsSection(
                        items = travelAgents,
                        title = "",
                        cardTitle = { it.prettyName },
                        cardSubtitle = {
                            it.contact?.organization?.name?.let { ag ->
                                ag + '\n'
                            }.orEmpty() + it.createdAt.formatDate(".")
                        },
                        cardActionButtons = {},
                    )
                }
            }

            if (isDeletable) {
                Spacer(Modifier.height(height = 2.spacing))
                DeleteNonUsedImage(
                    state = state,
                    onEvent = onEvent,
                )
            }
        }
    }
}